import moment from "moment";
import {
  RABINOVICH_WINDOW_END_EXCLUSIVE,
  RABINOVICH_WINDOW_START_EXCLUSIVE
} from "../calc/shared/haklaiConstants";
import {
  getHaklaiPrisaMaxYears,
  getHaklaiPrisaMinYears
} from "../calc/prisa/haklaiPrisaHelpers";

export const INHERITANCE_TESTATOR_BASIS_START_DATE = "1981-04-01";

const isDateOnOrAfter = (dateValue, cutoffDate) => {
  if (!dateValue) return false;
  const date = moment(dateValue);
  return date.isValid() && date.isSameOrAfter(moment(cutoffDate), "day");
};

export const shouldUseTestatorBoughtPropertyDate = (testatorPassingDate) => (
  isDateOnOrAfter(testatorPassingDate, INHERITANCE_TESTATOR_BASIS_START_DATE)
);

const isDateInRabinovichWindow = (dateValue) => {
  if (!dateValue) return false;
  const date = moment(dateValue);
  return Boolean(
    date.isValid()
    && date.isAfter(moment(RABINOVICH_WINDOW_START_EXCLUSIVE), "day")
    && date.isBefore(moment(RABINOVICH_WINDOW_END_EXCLUSIVE), "day")
  );
};

export const isHaklaiRabinovichEligible = (preCalc = {}) => {
  const acquisitionDetails = preCalc.acquisitionDetails || {};
  if (preCalc.wayOfGettingProperty === "inheritance") {
    return isDateInRabinovichWindow(acquisitionDetails.testatorBoughtPropertyDate);
  }
  if (preCalc.wayOfGettingProperty === "purchase" || preCalc.wayOfGettingProperty === "gift") {
    return isDateInRabinovichWindow(acquisitionDetails.deduDate);
  }
  return false;
};

export const resizeHaklaiPrisaDataPerYear = (person, numOfYears) => {
  const yearsCount = Number(numOfYears);
  const currentDataPerYear = Array.isArray(person.dataPerYear) ? person.dataPerYear : [];
  if (yearsCount === currentDataPerYear.length) return person;
  const defaultZikui = currentDataPerYear.length > 0 ? currentDataPerYear[0].zikui : 0;
  const dataPerYear = [];
  for (let i = 0; i < yearsCount; i++) {
    if (currentDataPerYear.length > i) {
      dataPerYear.push({ ...currentDataPerYear[i] });
    } else {
      dataPerYear.push({ income: null, zikui: defaultZikui });
    }
  }
  return { ...person, dataPerYear };
};

export const resizeHaklaiPrisaPersonsData = (prisa, numOfPersons, maxYears) => {
  const personsCount = Number(numOfPersons);
  const currentPersonsData = Array.isArray(prisa.personsData) ? prisa.personsData : [];
  if (personsCount === currentPersonsData.length) return prisa;
  const personsData = [];
  for (let i = 0; i < personsCount; i++) {
    let newPart;
    if (currentPersonsData.length > i) {
      newPart = {
        ...currentPersonsData[i],
        personOwningPercent: 1 / personsCount
      };
    } else {
      newPart = {
        personName: "",
        isMaximal: true,
        personOwningPercent: 1 / personsCount,
        birthOfClient: "",
        creditPointGender: "",
        creditPointResidency: "",
        dataPerYear: []
      };
    }
    newPart = resizeHaklaiPrisaDataPerYear(newPart, maxYears);
    personsData.push(newPart);
  }
  return { ...prisa, personsData };
};

export const applyHaklaiPrisaDependencies = (calcState) => {
  const prisa = calcState.mainCalc?.prisa;
  if (!prisa) return calcState;

  const maxYears = getHaklaiPrisaMaxYears(calcState.preCalc);
  const minYears = getHaklaiPrisaMinYears(calcState.preCalc);

  if (maxYears < 1 || minYears === 0) {
    if (!prisa.shouldTryPrisa && prisa.personsData.length === 0) return calcState;
    return {
      ...calcState,
      mainCalc: {
        ...calcState.mainCalc,
        prisa: { shouldTryPrisa: false, personsData: [] }
      }
    };
  }

  const needsTrim = prisa.personsData.some((p) => p.dataPerYear.length > maxYears);
  if (!needsTrim) return calcState;

  return {
    ...calcState,
    mainCalc: {
      ...calcState.mainCalc,
      prisa: {
        ...prisa,
        personsData: prisa.personsData.map((p) => resizeHaklaiPrisaDataPerYear(p, maxYears))
      }
    }
  };
};
